/**
 * Bingers kleine Scheune – Cookie Consent Manager
 * -----------------------------------------------
 * BFSG/WCAG 2.2 AA & TDDDG/GDPR 2026 compliant, self-hosted, no tracking.
 *
 * Features
 *  - Non-blocking banner (bottom bar) + blocking settings modal on demand.
 *  - Category-based consent (necessary, preferences, statistics, marketing).
 *  - Blocks `<script type="text/plain" data-cc-category="…">` and
 *    iframes with `data-cc-src="…"` until consent is granted.
 *  - Auto-activates newly injected blocked nodes (MutationObserver) so that
 *    e.g. Matomo tags dropped in later "just work" once their category is
 *    allowed.
 *  - Automatic cookie cleanup when a category is revoked (configurable
 *    cookie-name patterns per category).
 *  - Persists consent in localStorage (with cookie fallback flag purely for
 *    server-side awareness) and versions the payload so policy changes can
 *    force a re-prompt.
 *  - Public API: `window.bksConsent` + `bksconsent:change` CustomEvent.
 *  - Fully keyboard accessible, focus-trapped modal, reduced-motion aware.
 */

type ConsentCategory = 'necessary' | 'preferences' | 'statistics' | 'marketing';

interface ConsentState {
    v: number;
    ts: number;
    categories: Record<ConsentCategory, boolean>;
}

interface CategoryMeta {
    key: ConsentCategory;
    required: boolean;
    /** Regex patterns matching cookie names that belong to this category. */
    cookiePatterns: RegExp[];
}

const STORAGE_KEY = 'bks_cookie_consent';
/** Bump when categories/policy change – forces a re-prompt. */
const CONSENT_VERSION = 1;

const CATEGORIES: CategoryMeta[] = [
    { key: 'necessary', required: true, cookiePatterns: [/^PHPSESSID$/i, /^fe_typo_user$/i, /^be_typo_user$/i, /^bks_cookie_consent$/i, /^cf_/i] },
    { key: 'preferences', required: false, cookiePatterns: [/^bks_pref_/i] },
    { key: 'statistics', required: false, cookiePatterns: [/^_pk_/i, /^matomo_/i, /^MATOMO_/i, /^_ga/i, /^_gid$/i] },
    { key: 'marketing', required: false, cookiePatterns: [/^_fbp$/i, /^fr$/i, /^IDE$/i, /^NID$/i] },
];

const defaultState = (): ConsentState => ({
    v: CONSENT_VERSION,
    ts: 0,
    categories: { necessary: true, preferences: false, statistics: false, marketing: false },
});

const loadState = (): ConsentState | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as ConsentState;
        if (!parsed || parsed.v !== CONSENT_VERSION) return null;
        parsed.categories.necessary = true;
        return parsed;
    } catch {
        return null;
    }
};

const saveState = (state: ConsentState): void => {
    state.ts = Date.now();
    state.categories.necessary = true;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        /* quota / private mode – ignore */
    }
};

const getDomainForCookieDeletion = (): string => {
    const host = window.location.hostname;
    const parts = host.split('.');
    if (parts.length >= 2) {
        return '.' + parts.slice(-2).join('.');
    }
    return host;
};

const deleteCookie = (name: string): void => {
    const paths = ['/', window.location.pathname];
    const domains = ['', window.location.hostname, getDomainForCookieDeletion()];
    for (const path of paths) {
        for (const domain of domains) {
            const domainPart = domain ? `; domain=${domain}` : '';
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domainPart}; SameSite=Lax`;
        }
    }
};

const purgeDisallowedCookies = (state: ConsentState): void => {
    const cookies = document.cookie.split(';').map(c => c.split('=')[0].trim()).filter(Boolean);
    for (const name of cookies) {
        const owner = CATEGORIES.find(cat => cat.cookiePatterns.some(rx => rx.test(name)));
        if (!owner) continue; // unknown cookies: leave alone
        if (!state.categories[owner.key]) {
            deleteCookie(name);
        }
    }
};

/**
 * Activate a single blocked node (<script type="text/plain"> or iframe with
 * data-cc-src) by rewriting it in-place so the browser executes it.
 */
const activateNode = (node: Element): void => {
    if (node.tagName === 'SCRIPT') {
        const original = node as HTMLScriptElement;
        if (original.dataset.ccActivated === '1') return;
        const fresh = document.createElement('script');
        // Copy attributes except the placeholder type + our markers
        for (const attr of Array.from(original.attributes)) {
            if (attr.name === 'type' && /text\/plain/i.test(attr.value)) continue;
            if (attr.name.startsWith('data-cc-')) continue;
            fresh.setAttribute(attr.name, attr.value);
        }
        if (original.src) {
            fresh.src = original.src;
        } else {
            fresh.textContent = original.textContent ?? '';
        }
        fresh.dataset.ccActivated = '1';
        original.parentNode?.replaceChild(fresh, original);
        return;
    }

    if (node.tagName === 'IFRAME') {
        const iframe = node as HTMLIFrameElement;
        if (iframe.dataset.ccActivated === '1') return;
        const src = iframe.getAttribute('data-cc-src');
        if (src) {
            iframe.setAttribute('src', src);
            iframe.removeAttribute('data-cc-src');
            iframe.dataset.ccActivated = '1';
        }
    }
};

const activateAllowedNodes = (state: ConsentState, root: ParentNode = document): void => {
    const candidates = root.querySelectorAll<HTMLElement>(
        'script[data-cc-category][type="text/plain"], iframe[data-cc-category][data-cc-src]',
    );
    candidates.forEach(el => {
        const cat = el.dataset.ccCategory as ConsentCategory | undefined;
        if (!cat) return;
        if (state.categories[cat]) {
            activateNode(el);
        }
    });
};

const dispatchChange = (state: ConsentState): void => {
    window.dispatchEvent(
        new CustomEvent('bksconsent:change', { detail: { ...state, categories: { ...state.categories } } }),
    );
};

/* ── UI ─────────────────────────────────────────────────────────────── */

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface UIRefs {
    banner: HTMLElement | null;
    modal: HTMLElement | null;
    dialog: HTMLElement | null;
    toggles: Map<ConsentCategory, HTMLInputElement>;
}

const ui: UIRefs = { banner: null, modal: null, dialog: null, toggles: new Map() };

const showBanner = (): void => {
    if (!ui.banner) return;
    ui.banner.hidden = false;
    ui.banner.classList.remove('translate-y-full', 'opacity-0');
};

const hideBanner = (): void => {
    if (!ui.banner) return;
    ui.banner.classList.add('translate-y-full', 'opacity-0');
    window.setTimeout(() => {
        if (ui.banner) ui.banner.hidden = true;
    }, 250);
};

let lastFocusedBeforeModal: HTMLElement | null = null;

const trapFocus = (e: KeyboardEvent): void => {
    if (!ui.dialog || e.key !== 'Tab') return;
    const focusable = Array.from(ui.dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
    }
};

const onModalKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
    } else if (e.key === 'Tab') {
        trapFocus(e);
    }
};

const openModal = (state: ConsentState): void => {
    if (!ui.modal || !ui.dialog) return;
    lastFocusedBeforeModal = document.activeElement as HTMLElement | null;

    // Sync toggles with current state
    ui.toggles.forEach((input, cat) => {
        input.checked = state.categories[cat];
        input.disabled = CATEGORIES.find(c => c.key === cat)?.required ?? false;
    });

    ui.modal.hidden = false;
    document.body.classList.add('overflow-hidden');
    ui.modal.addEventListener('keydown', onModalKeydown);

    requestAnimationFrame(() => {
        const firstFocus = ui.dialog?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        firstFocus?.focus();
    });
};

const closeModal = (): void => {
    if (!ui.modal) return;
    ui.modal.hidden = true;
    document.body.classList.remove('overflow-hidden');
    ui.modal.removeEventListener('keydown', onModalKeydown);
    lastFocusedBeforeModal?.focus();
    lastFocusedBeforeModal = null;
};

/* ── Controller ─────────────────────────────────────────────────────── */

const applyState = (state: ConsentState, opts: { purge?: boolean } = {}): void => {
    saveState(state);
    if (opts.purge) purgeDisallowedCookies(state);
    activateAllowedNodes(state);
    dispatchChange(state);
};

const acceptAll = (): ConsentState => {
    const state = defaultState();
    (Object.keys(state.categories) as ConsentCategory[]).forEach(k => (state.categories[k] = true));
    applyState(state);
    return state;
};

const rejectAll = (): ConsentState => {
    const state = defaultState();
    applyState(state, { purge: true });
    return state;
};

const saveFromModal = (): ConsentState => {
    const state = loadState() ?? defaultState();
    ui.toggles.forEach((input, cat) => {
        state.categories[cat] = input.checked || cat === 'necessary';
    });
    applyState(state, { purge: true });
    return state;
};

const wireBannerButtons = (): void => {
    if (!ui.banner) return;
    ui.banner.querySelector<HTMLButtonElement>('[data-cc-action="accept-all"]')?.addEventListener('click', () => {
        acceptAll();
        hideBanner();
    });
    ui.banner.querySelector<HTMLButtonElement>('[data-cc-action="reject-all"]')?.addEventListener('click', () => {
        rejectAll();
        hideBanner();
    });
    ui.banner.querySelector<HTMLButtonElement>('[data-cc-action="customize"]')?.addEventListener('click', () => {
        openModal(loadState() ?? defaultState());
    });
};

const wireModalButtons = (): void => {
    if (!ui.modal) return;
    ui.modal.querySelector<HTMLButtonElement>('[data-cc-action="accept-all"]')?.addEventListener('click', () => {
        acceptAll();
        closeModal();
        hideBanner();
    });
    ui.modal.querySelector<HTMLButtonElement>('[data-cc-action="reject-all"]')?.addEventListener('click', () => {
        rejectAll();
        closeModal();
        hideBanner();
    });
    ui.modal.querySelector<HTMLButtonElement>('[data-cc-action="save"]')?.addEventListener('click', () => {
        saveFromModal();
        closeModal();
        hideBanner();
    });
    ui.modal.querySelector<HTMLButtonElement>('[data-cc-action="close"]')?.addEventListener('click', () => {
        closeModal();
    });

    // Click on backdrop (element with [data-cc-backdrop]) closes modal
    ui.modal.addEventListener('click', e => {
        if ((e.target as HTMLElement).hasAttribute('data-cc-backdrop')) {
            closeModal();
        }
    });
};

const wireExternalOpeners = (): void => {
    document.querySelectorAll<HTMLElement>('[data-cc-open]').forEach(btn => {
        btn.addEventListener('click', e => {
            e.preventDefault();
            openModal(loadState() ?? defaultState());
        });
    });
};

const observeDOM = (getState: () => ConsentState): void => {
    const observer = new MutationObserver(mutations => {
        for (const m of mutations) {
            m.addedNodes.forEach(node => {
                if (!(node instanceof Element)) return;
                activateAllowedNodes(getState(), node);
                // Also check the node itself (MutationObserver only gives children via querySelectorAll on the subtree)
                if (node.matches?.('script[data-cc-category][type="text/plain"], iframe[data-cc-category][data-cc-src]')) {
                    const cat = (node as HTMLElement).dataset.ccCategory as ConsentCategory | undefined;
                    if (cat && getState().categories[cat]) {
                        activateNode(node);
                    }
                }
            });
        }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
};

const init = (): void => {
    ui.banner = document.getElementById('bks-cookie-banner');
    ui.modal = document.getElementById('bks-cookie-modal');
    ui.dialog = ui.modal?.querySelector<HTMLElement>('[role="dialog"]') ?? null;

    if (ui.modal) {
        ui.modal.querySelectorAll<HTMLInputElement>('input[data-cc-toggle]').forEach(input => {
            const cat = input.dataset.ccToggle as ConsentCategory | undefined;
            if (cat) ui.toggles.set(cat, input);
        });
    }

    wireBannerButtons();
    wireModalButtons();
    wireExternalOpeners();

    const existing = loadState();
    const state = existing ?? defaultState();

    // Activate whatever is already allowed (necessary always, plus stored).
    activateAllowedNodes(state);

    // Watch for future injected tags
    observeDOM(() => loadState() ?? defaultState());

    if (!existing) {
        showBanner();
    }

    // Public API
    (window as unknown as { bksConsent: unknown }).bksConsent = {
        getState: () => loadState() ?? defaultState(),
        accept: (cats: ConsentCategory[]) => {
            const s = loadState() ?? defaultState();
            cats.forEach(c => (s.categories[c] = true));
            applyState(s);
            hideBanner();
            return s;
        },
        revoke: (cats: ConsentCategory[]) => {
            const s = loadState() ?? defaultState();
            cats.forEach(c => {
                if (c !== 'necessary') s.categories[c] = false;
            });
            applyState(s, { purge: true });
            return s;
        },
        acceptAll: () => { const s = acceptAll(); hideBanner(); return s; },
        rejectAll: () => { const s = rejectAll(); hideBanner(); return s; },
        openSettings: () => openModal(loadState() ?? defaultState()),
        hasConsent: (cat: ConsentCategory) => (loadState() ?? defaultState()).categories[cat],
    };
};

// Run as early as possible so blocked scripts near the top of the page
// are activated before they would otherwise have executed on DOMContentLoaded.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
    init();
}

export {};

